import React from 'react';
import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';

import {
  ResourceType,
  ResourceTypeResponse,
} from 'types/entityTypes/ResourceTypes';

const getResourceTypes = async () => {
  const url = generateURL(SPARK_RESOURCE_TYPES.RESOURCE_TYPES);

  return fetchWrapper<ResourceTypeResponse>({
    url: url,
    method: 'GET',
  });
};

export function useResourceTypes() {
  const queryResult = useQuery(
    SPARK_RESOURCE_TYPES.RESOURCE_TYPES,
    getResourceTypes,
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to get Resource Types: ${error.message}`);
        }
      },
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const { data } = queryResult;
  const { data: resourceTypes } = data || {};

  const idMap = React.useMemo(() => {
    return resourceTypes?.reduce<Record<string, ResourceType>>((acc, curr) => {
      return { ...acc, [curr.id]: curr };
    }, {});
  }, [resourceTypes]);

  const idByKeyMap = React.useMemo(() => {
    return resourceTypes?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.attributes.key]: curr.id };
    }, {});
  }, [resourceTypes]);

  const keyByIDMap = React.useMemo(() => {
    return resourceTypes?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.id]: curr.attributes.key };
    }, {});
  }, [resourceTypes]);

  return {
    resourceShareIdMap: idMap,
    resourceTypes,
    idByKeyMap,
    keyByIDMap,
    ...queryResult,
  };
}
